import React, { useRef, useState, useEffect } from "react";
import { isMobile, isTablet } from "react-device-detect";

interface ZoomPanPinchProps {
  children: React.ReactNode;
}

const ZoomPanPinch: React.FC<ZoomPanPinchProps> = ({ children }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);
  const [translate, setTranslate] = useState({ x: 0, y: 0 });
  const [origin, setOrigin] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [startDrag, setStartDrag] = useState({ x: 0, y: 0 });
  const [lastTap, setLastTap] = useState<number | null>(null);
  const [initialDistance, setInitialDistance] = useState<number | null>(null);
  const [initialScale, setInitialScale] = useState<number | null>(null);
  const [isPinching, setIsPinching] = useState(false);

  useEffect(() => {
    // Reset state when children change
    setScale(1);
    setTranslate({ x: 0, y: 0 });
    setOrigin({ x: 0, y: 0 });
  }, [children]);

  useEffect(() => {
    const container = containerRef.current;

    const handleWheel = (e: WheelEvent) => {
      e.preventDefault();
      const delta = Math.sign(e.deltaY) * -0.1;
      const newScale = Math.max(1, Math.min(scale + delta, 5)); // Minimum scale is 1

      const rect = container?.getBoundingClientRect()!;
      const offsetX = e.clientX - rect.left;
      const offsetY = e.clientY - rect.top;

      const newOrigin = {
        x: (offsetX - origin.x) / scale + origin.x,
        y: (offsetY - origin.y) / scale + origin.y,
      };

      setScale(newScale);
      setOrigin(newOrigin);
    };

    const handleTouchStart = (e: TouchEvent) => {
      if (e.touches.length === 2) {
        const touch1 = e.touches[0];
        const touch2 = e.touches[1];
        const initialDistance = Math.hypot(
          touch1.clientX - touch2.clientX,
          touch1.clientY - touch2.clientY
        );
        setInitialDistance(initialDistance);
        setInitialScale(scale);
        setIsPinching(true);
      } else if (e.touches.length === 1) {
        const currentTime = new Date().getTime();
        const tapInterval = 300; // Maximum interval for double tap

        if (lastTap && currentTime - lastTap < tapInterval) {
          handleDoubleTap(e);
          setLastTap(null);
        } else {
          setLastTap(currentTime);
        }

        setIsDragging(true);
        const touch = e.touches[0];
        setStartDrag({
          x: touch.clientX - translate.x,
          y: touch.clientY - translate.y,
        });
      }
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (
        e.touches.length === 2 &&
        initialDistance !== null &&
        initialScale !== null
      ) {
        const touch1 = e.touches[0];
        const touch2 = e.touches[1];
        const currentDistance = Math.hypot(
          touch1.clientX - touch2.clientX,
          touch1.clientY - touch2.clientY
        );
        const delta = (currentDistance - initialDistance) * 0.01;
        const newScale = Math.max(1, Math.min(initialScale + delta, 5));

        const rect = container?.getBoundingClientRect()!;
        const offsetX = (touch1.clientX + touch2.clientX) / 2 - rect.left;
        const offsetY = (touch1.clientY + touch2.clientY) / 2 - rect.top;

        const newOrigin = {
          x: (offsetX - origin.x) / scale + origin.x,
          y: (offsetY - origin.y) / scale + origin.y,
        };

        setScale(newScale);
        setOrigin(newOrigin);
      } else if (isDragging && e.touches.length === 1) {
        const touch = e.touches[0];
        setTranslate({
          x: touch.clientX - startDrag.x,
          y: touch.clientY - startDrag.y,
        });
      }
    };

    const handleTouchEnd = () => {
      setIsDragging(false);
      setInitialDistance(null);
      setInitialScale(null);
      setIsPinching(false);
    };

    const handleMouseDown = (e: MouseEvent) => {
      e.preventDefault();
      setIsDragging(true);
      setStartDrag({ x: e.clientX - translate.x, y: e.clientY - translate.y });
    };

    const handleMouseMove = (e: MouseEvent) => {
      if (!isDragging) return;
      e.preventDefault();
      setTranslate({
        x: e.clientX - startDrag.x,
        y: e.clientY - startDrag.y,
      });
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    const handleDoubleClick = (e: MouseEvent) => {
      const rect = container?.getBoundingClientRect()!;
      const offsetX = e.clientX - rect.left;
      const offsetY = e.clientY - rect.top;

      const newScale = scale >= 2 ? 1 : scale + 1; // Toggle between zoom in and reset
      const newOrigin = {
        x: (offsetX - origin.x) / scale + origin.x,
        y: (offsetY - origin.y) / scale + origin.y,
      };

      setScale(newScale);
      setOrigin(newOrigin);
    };

    const handleDoubleTap = (e: TouchEvent) => {
      const touch = e.touches[0];
      const rect = container?.getBoundingClientRect()!;
      const offsetX = touch.clientX - rect.left;
      const offsetY = touch.clientY - rect.top;

      const newScale = scale >= 2 ? 1 : scale + 1; // Toggle between zoom in and reset
      const newOrigin = {
        x: (offsetX - origin.x) / scale + origin.x,
        y: (offsetY - origin.y) / scale + origin.y,
      };

      setScale(newScale);
      setOrigin(newOrigin);
    };

    // Check if it's a mobile/tablet device
    const isTouchDevice = isMobile || isTablet;

    if (isTouchDevice) {
      container?.addEventListener("touchstart", handleTouchStart);
      container?.addEventListener("touchmove", handleTouchMove);
      container?.addEventListener("touchend", handleTouchEnd);
    } else {
      container?.addEventListener("wheel", handleWheel);
      container?.addEventListener("mousedown", handleMouseDown);
      container?.addEventListener("mousemove", handleMouseMove);
      container?.addEventListener("mouseup", handleMouseUp);
      container?.addEventListener("mouseleave", handleMouseUp);
      container?.addEventListener("dblclick", handleDoubleClick);
    }

    return () => {
      if (isTouchDevice) {
        container?.removeEventListener("touchstart", handleTouchStart);
        container?.removeEventListener("touchmove", handleTouchMove);
        container?.removeEventListener("touchend", handleTouchEnd);
      } else {
        container?.removeEventListener("wheel", handleWheel);
        container?.removeEventListener("mousedown", handleMouseDown);
        container?.removeEventListener("mousemove", handleMouseMove);
        container?.removeEventListener("mouseup", handleMouseUp);
        container?.removeEventListener("mouseleave", handleMouseUp);
        container?.removeEventListener("dblclick", handleDoubleClick);
      }
    };
  }, [
    scale,
    translate,
    origin,
    isDragging,
    startDrag,
    lastTap,
    initialDistance,
    initialScale,
    isPinching,
  ]);

  return (
    <div
      ref={containerRef}
      style={{
        width: "100%",
        height: "100%",
        overflow: "hidden",
        position: "relative",
        touchAction: "none",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <div
        style={{
          transform: `translate(${translate.x}px, ${translate.y}px) scale(${scale})`,
          transformOrigin: `${origin.x}px ${origin.y}px`,
          transition:
            isDragging || isPinching ? "none" : "transform 0.1s ease-out",
          cursor: isDragging ? "grabbing" : "grab",
        }}
      >
        {children}
      </div>
    </div>
  );
};

export default ZoomPanPinch;
